import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, ExternalLink, Info, AlertTriangle, Lightbulb,
  Sparkles, GraduationCap,
} from 'lucide-react';

import { guideBySlug, guideBySlug as findGuide } from '@/data/knowledgeBase';
import type { Guide, GuideBlock, GuideLink, GuideLevel } from '@/data/knowledgeBase';
import { ShareLinkButton } from '@/components/help/ShareLinkButton';
import { PublicViewContext, useIsPublicView } from './kbView';
import { usePermission } from '@/hooks/usePermission';
import { AppRoutes } from '@/constants/routes';
import { cn } from '@/utils/cn';

/**
 * One guide.
 *
 * Links to screens are filtered by the reader's permissions: a guide explains the whole process to
 * everyone, but only offers to open the screens that person can actually reach. Showing a link that
 * lands on Access Denied teaches the reader nothing except that the help is lying to them.
 */

const LevelPill = ({ level }: { level: GuideLevel }) => (
  <span className={cn(
    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
    level === 'Basics'
      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
      : 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  )}>
    {level === 'Basics' ? <Sparkles className="h-3 w-3" /> : <GraduationCap className="h-3 w-3" />}
    {level}
  </span>
);

/** A screen link, rendered only when the reader may open it. */
const ScreenLinks = ({ links, title }: { links: GuideLink[]; title?: string }) => {
  const { hasPermission } = usePermission();
  const isPublic = useIsPublicView();

  // On the public mirror there is nothing to open — and see kbView.ts for why this is not left
  // to the permission check alone.
  const allowed = isPublic ? [] : links.filter(l => !l.permission || hasPermission(l.permission));
  if (allowed.length === 0) return null;

  return (
    <div className="my-4">
      {title && (
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-gray-400">
          {title}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {allowed.map(l => (
          <Link key={l.route} to={l.route}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 bg-brand-50 px-3 py-1.5
                       text-sm font-medium text-brand-700 transition-colors hover:bg-brand-100
                       dark:border-brand-700 dark:bg-brand-900/30 dark:text-brand-300 dark:hover:bg-brand-900/50">
            <ExternalLink className="h-3.5 w-3.5" /> {l.label}
          </Link>
        ))}
      </div>
    </div>
  );
};

const CALLOUT = {
  note: { icon: Info,          cls: 'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-800 dark:bg-sky-900/20 dark:text-sky-200' },
  warn: { icon: AlertTriangle, cls: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200' },
  tip:  { icon: Lightbulb,     cls: 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200' },
} as const;

const Block = ({ block }: { block: GuideBlock }) => {
  switch (block.kind) {
    case 'heading':
      return <h2 className="mb-2 mt-8 text-lg font-semibold text-slate-900 dark:text-white">{block.text}</h2>;

    case 'para':
      return <p className="mb-3 leading-relaxed text-slate-700 dark:text-slate-300">{block.text}</p>;

    case 'list':
      return block.ordered ? (
        <ol className="mb-4 list-decimal space-y-1.5 pl-5 text-slate-700 dark:text-slate-300">
          {block.items.map((i, n) => <li key={n} className="leading-relaxed">{i}</li>)}
        </ol>
      ) : (
        <ul className="mb-4 list-disc space-y-1.5 pl-5 text-slate-700 dark:text-slate-300">
          {block.items.map((i, n) => <li key={n} className="leading-relaxed">{i}</li>)}
        </ul>
      );

    case 'steps':
      return (
        <ol className="mb-5 space-y-4">
          {block.steps.map((s, n) => (
            <li key={n} className="flex gap-4">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs
                               font-semibold text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
                {n + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900 dark:text-white">{s.title}</p>
                {s.where && (
                  <p className="mt-0.5 font-mono text-[11px] text-brand-600 dark:text-brand-400">{s.where}</p>
                )}
                {s.detail && (
                  <p className="mt-1 leading-relaxed text-slate-600 dark:text-slate-300">{s.detail}</p>
                )}
                {/* Optional per-step screenshot: drop a file at this path under /public and it appears. */}
                {s.image && (
                  <img src={s.image} alt={s.title} loading="lazy"
                    className="mt-3 max-w-full rounded-lg border border-slate-200 shadow-sm dark:border-slate-700" />
                )}
              </div>
            </li>
          ))}
        </ol>
      );

    case 'callout': {
      const { icon: Icon, cls } = CALLOUT[block.tone];
      return (
        <div className={cn('my-5 flex gap-3 rounded-lg border px-4 py-3', cls)}>
          <Icon className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0">
            {block.title && <p className="mb-0.5 text-sm font-semibold">{block.title}</p>}
            <p className="text-sm leading-relaxed">{block.text}</p>
          </div>
        </div>
      );
    }

    case 'table':
      return (
        <div className="my-5 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-brand-600 text-left text-white">
                {block.headers.map((h, n) => (
                  <th key={n} className="px-4 py-2.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {block.rows.map((r, n) => (
                <tr key={n} className="bg-white dark:bg-slate-800">
                  {r.map((c, m) => (
                    <td key={m} className={cn('px-4 py-2.5 align-top leading-relaxed',
                      m === 0 ? 'font-medium text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300')}>
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'links':
      return <ScreenLinks links={block.links} title={block.title} />;

    case 'image':
      return (
        <figure className="my-5">
          <img src={block.src} alt={block.caption ?? ''} loading="lazy"
            className="max-w-full rounded-lg border border-slate-200 shadow-sm dark:border-slate-700" />
          {block.caption && (
            <figcaption className="mt-2 text-xs text-slate-500 dark:text-gray-400">{block.caption}</figcaption>
          )}
        </figure>
      );
  }
};

interface GuideArticleProps {
  guide: Guide;
  /** Where "read next" and the back link point — the in-app tree or the public mirror. */
  guideHref: (slug: string) => string;
  indexHref: string;
  /** Offer the public link. Suppressed on the public page, where the reader is already on it. */
  shareable?: boolean;
  /** Rendering the public mirror: no screen links, whoever is reading. */
  publicView?: boolean;
}

/**
 * The guide itself, rendered identically in the app and on the public mirror.
 *
 * One renderer on purpose: a public copy that drifts from what staff read is worse than no public
 * copy. The only thing that differs is where links go — and screen links vanish by themselves on
 * the public route, because a signed-out reader holds no permissions and every link is gated.
 */
export const GuideArticle = ({
  guide, guideHref, indexHref, shareable = true, publicView = false,
}: GuideArticleProps) => {
  const related = (guide.related ?? []).map(findGuide).filter(Boolean);

  return (
    <PublicViewContext.Provider value={publicView}>
      <Link to={indexHref}
        className="mb-5 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-600 dark:text-gray-400">
        <ArrowLeft className="h-4 w-4" /> Knowledge base
      </Link>

      <article className="max-w-3xl">
        <div className="mb-1 flex flex-wrap items-center gap-3">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-white">
            <BookOpen className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            {guide.title}
          </h1>
          <LevelPill level={guide.level} />
          {shareable && <ShareLinkButton slug={guide.slug} className="ml-auto" />}
        </div>

        <p className="leading-relaxed text-slate-600 dark:text-slate-300">{guide.summary}</p>
        <p className="mt-1 text-sm text-slate-400">
          Find these screens under: <span className="font-medium text-slate-500 dark:text-gray-400">{guide.menuPath}</span>
        </p>

        {guide.screens && <ScreenLinks links={guide.screens} title="Open a screen" />}

        <div className="mt-6">
          {guide.blocks.map((b, n) => <Block key={n} block={b} />)}
        </div>

        {related.length > 0 && (
          <div className="mt-10 border-t border-slate-200 pt-6 dark:border-slate-700">
            <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-gray-400">
              Read next
            </h2>
            <div className="flex flex-wrap gap-2">
              {related.map(r => (
                <Link key={r!.slug} to={guideHref(r!.slug)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700
                             transition-colors hover:border-brand-300 hover:bg-brand-50/40
                             dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  {r!.title}
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </PublicViewContext.Provider>
  );
};

/** "That guide does not exist" — shared, so a bad public link fails the same way a bad app one does. */
export const GuideNotFound = ({ indexHref }: { indexHref: string }) => (
  <>
    <Link to={indexHref} className="mb-4 inline-flex items-center gap-1.5 text-sm text-brand-600 dark:text-brand-400">
      <ArrowLeft className="h-4 w-4" /> Knowledge base
    </Link>
    <p className="text-slate-600 dark:text-slate-300">That guide does not exist.</p>
  </>
);

export const GuidePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const guide = slug ? guideBySlug(slug) : undefined;

  return (
    <div className="p-6">
      {guide
        ? <GuideArticle
            guide={guide}
            indexHref={AppRoutes.HELP}
            guideHref={s => AppRoutes.HELP_GUIDE.replace(':slug', s)} />
        : <GuideNotFound indexHref={AppRoutes.HELP} />}
    </div>
  );
};
